/// <reference path="../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// Forward-message conversation picker. Opened from the message action
// menu's "Forward" entry. Lists the user's conversations; picking one
// forwards the source message into it (the host owns the actual send).
//
// Rendered through the web body portal (instant, no RN-Web Modal mount
// gate); RN Modal on native.

import React, { useMemo, useState } from "react";
import { Modal, Pressable, StyleSheet, Text, TextInput, View } from "react-native";
import { Search, X } from "lucide-react-native";
import { useColorScheme } from "../foundations/colorScheme";
import { tokens } from "../design-system/tokens";
import { Avatar } from "../components/Avatar";
import { WebPortal } from "./composer/WebPortal";

const IS_WEB = typeof document !== "undefined" && typeof window !== "undefined";

export interface ForwardTargetConversation {
  readonly id: string;
  readonly name: string;
}

export interface ForwardOverlayProps {
  readonly open: boolean;
  readonly conversations: ReadonlyArray<ForwardTargetConversation>;
  readonly onPick: (conversationId: string) => void;
  readonly onClose: () => void;
}

export const ForwardOverlay = React.memo<ForwardOverlayProps>(
  ({ open, conversations, onPick, onClose }) => {
    const scheme = useColorScheme();
    const c = tokens.color.semantic[scheme === "dark" ? "dark" : "light"];
    const [query, setQuery] = useState("");

    const filtered = useMemo(() => {
      const q = query.trim().toLowerCase();
      if (q.length === 0) return conversations;
      return conversations.filter((conv) => conv.name.toLowerCase().includes(q));
    }, [conversations, query]);

    if (!open) return null;

    const card = (
      <Pressable
        accessibilityRole="none"
        onPress={onClose}
        className="flex-1 items-center justify-center bg-black/70 p-6"
      >
        <Pressable
          accessibilityRole="none"
          onPress={(e) => e.stopPropagation?.()}
          className="w-full max-w-[420px] max-h-[70%] rounded-[16px] overflow-hidden bg-light-surface-primary dark:bg-[#15171C] border border-light-border-secondary dark:border-white/[0.06]"
        >
          {/* Header */}
          <View className="flex-row items-center justify-between px-lg pt-lg pb-md">
            <Text className="font-sans font-bold text-headline text-light-text-primary dark:text-white">
              Forward to…
            </Text>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Close"
              onPress={onClose}
              hitSlop={8}
              className="w-8 h-8 rounded-lg items-center justify-center hover:bg-light-surface-tertiary dark:hover:bg-white/[0.06]"
            >
              <X size={16} strokeWidth={2} color={c.icon.muted} />
            </Pressable>
          </View>

          {/* Search */}
          <View className="px-lg pb-md">
            <View className="flex-row items-center h-[36px] rounded-[8px] px-md gap-sm bg-light-surface-panel dark:bg-[#222429] border border-light-border-subtle dark:border-white/[0.06]">
              <Search size={14} strokeWidth={1.75} color={c.icon.muted} />
              <TextInput
                value={query}
                onChangeText={setQuery}
                placeholder="Search chats…"
                placeholderTextColor={c.text.tertiary}
                className="flex-1 font-sans text-[13px] text-light-text-primary dark:text-white outline-none"
                accessibilityLabel="Search conversations to forward to"
              />
            </View>
          </View>

          {/* Conversation list */}
          <View className="pb-lg px-sm">
            {filtered.length === 0 ? (
              <Text className="px-md py-lg text-center font-sans text-[13px] text-light-text-tertiary dark:text-[#B5BAC3]">
                No conversations found.
              </Text>
            ) : (
              filtered.map((conv) => (
                <Pressable
                  key={conv.id}
                  accessibilityRole="button"
                  accessibilityLabel={`Forward to ${conv.name}`}
                  onPress={() => onPick(conv.id)}
                  className="flex-row items-center gap-sm px-md py-2 rounded-[10px] hover:bg-light-surface-tertiary dark:hover:bg-white/[0.05]"
                >
                  <Avatar initials={initialsOf(conv.name)} size="small" shape="rounded" />
                  <Text
                    numberOfLines={1}
                    className="flex-1 font-sans text-[14px] text-light-text-primary dark:text-white"
                  >
                    {conv.name}
                  </Text>
                </Pressable>
              ))
            )}
          </View>
        </Pressable>
      </Pressable>
    );

    if (IS_WEB) {
      return (
        <WebPortal>
          <View style={StyleSheet.absoluteFill}>{card}</View>
        </WebPortal>
      );
    }
    return (
      <Modal visible={open} transparent animationType="none" onRequestClose={onClose} accessibilityViewIsModal>
        {card}
      </Modal>
    );
  },
);

ForwardOverlay.displayName = "ForwardOverlay";

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return (parts[0]?.slice(0, 2) ?? "?").toUpperCase();
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase();
}
